import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { evalFunctionApi, EvalFunctionConfigSchema } from '@/services/evalFunctionApi';
import { Objective } from './ObjectivesEditor';

// Types
export interface EvalFunctionOption {
  id: string;
  name: string;
  display_name?: string;
}

export interface EvaluationConfig {
  // Correctness check
  correctnessFunction: string | null; // function ID or null for none
  correctnessFunctionConfig?: Record<string, unknown>; // config values for the function

  // Quality scoring
  qualityMode: 'none' | 'llm_judge' | 'custom_function';
  qualityFunctionId?: string; // if custom_function
  qualityFunctionConfig?: Record<string, unknown>; // config values for the function
  judgeAgent?: string; // path to judge agent markdown file
  judgeModel?: string;
  judgePassThreshold?: number;

  // Secondary metrics
  optimizeLatency: boolean;
  optimizeCost: boolean;
  optimizeToolCalls: boolean;
}

export interface JudgeOption {
  id: string;
  name: string;
  path: string;
}

interface EvaluationSetupProps {
  config: EvaluationConfig;
  onChange: (config: EvaluationConfig) => void;
  availableEvalFunctions: EvalFunctionOption[];
  availableJudges?: JudgeOption[];
  availableModels?: { value: string; label: string }[];
  isOptimization?: boolean; // Show optimizer-specific messaging
}

const NONE_VALUE = '__none__';

/**
 * Convert EvaluationConfig to Objective[] for the backend
 */
export function configToObjectives(config: EvaluationConfig): Objective[] {
  const objectives: Objective[] = [];

  // Correctness check = required constraint (weight 0, must pass)
  if (config.correctnessFunction) {
    objectives.push({
      name: 'correctness',
      source: 'eval_function',
      source_key: config.correctnessFunction,
      direction: 'maximize',
      weight: 0,
      required: true,
      target_threshold: 1.0,
      source_config: config.correctnessFunctionConfig,
    });
  }

  // Quality scoring
  if (config.qualityMode === 'llm_judge') {
    objectives.push({
      name: 'quality',
      source: 'judge_score',
      direction: 'maximize',
      weight: 1.0,
    });
  } else if (config.qualityMode === 'custom_function' && config.qualityFunctionId) {
    objectives.push({
      name: 'quality',
      source: 'eval_function',
      source_key: config.qualityFunctionId,
      direction: 'maximize',
      weight: 1.0,
      source_config: config.qualityFunctionConfig,
    });
  }

  // Secondary metrics (lower weights)
  if (config.optimizeLatency) {
    objectives.push({
      name: 'latency',
      source: 'metric',
      source_key: 'latency_ms',
      direction: 'minimize',
      weight: 0.3,
    });
  }

  if (config.optimizeCost) {
    objectives.push({
      name: 'cost',
      source: 'metric',
      source_key: 'total_tokens',
      direction: 'minimize',
      weight: 0.2,
    });
  }

  if (config.optimizeToolCalls) {
    objectives.push({
      name: 'tool_efficiency',
      source: 'metric',
      source_key: 'tool_call_count',
      direction: 'minimize',
      weight: 0.1,
    });
  }

  // Normalize weights if we have quality + metrics
  const totalWeight = objectives.reduce((sum, o) => sum + (o.weight || 0), 0);
  if (totalWeight > 0 && totalWeight !== 1.0) {
    objectives.forEach(o => {
      if (o.weight && o.weight > 0) {
        o.weight = o.weight / totalWeight;
      }
    });
  }

  // If no objectives at all, default to latency
  if (objectives.length === 0) {
    objectives.push({
      name: 'latency',
      source: 'metric',
      source_key: 'latency_ms',
      direction: 'minimize',
      weight: 1.0,
    });
  }

  return objectives;
}

/**
 * Convert Objective[] back to EvaluationConfig (for loading existing configs)
 */
export function objectivesToConfig(objectives: Objective[]): EvaluationConfig {
  const config: EvaluationConfig = {
    correctnessFunction: null,
    qualityMode: 'none',
    optimizeLatency: false,
    optimizeCost: false,
    optimizeToolCalls: false,
  };

  for (const obj of objectives) {
    // Correctness = required eval_function
    if (obj.source === 'eval_function' && obj.required) {
      config.correctnessFunction = obj.source_key || null;
      config.correctnessFunctionConfig = obj.source_config;
    }
    // Quality from judge
    else if (obj.source === 'judge_score') {
      config.qualityMode = 'llm_judge';
    }
    // Quality from non-required eval function
    else if (obj.source === 'eval_function' && !obj.required && obj.name === 'quality') {
      config.qualityMode = 'custom_function';
      config.qualityFunctionId = obj.source_key;
      config.qualityFunctionConfig = obj.source_config;
    }
    // Metrics
    else if (obj.source === 'metric') {
      if (obj.source_key === 'latency_ms') config.optimizeLatency = true;
      if (obj.source_key === 'total_tokens') config.optimizeCost = true;
      if (obj.source_key === 'tool_call_count') config.optimizeToolCalls = true;
    }
  }

  return config;
}

export function EvaluationSetup({
  config,
  onChange,
  availableEvalFunctions,
  availableJudges = [],
  availableModels = [],
  isOptimization = true,
}: EvaluationSetupProps) {
  // Cache for function schemas
  const [functionSchemas, setFunctionSchemas] = useState<Record<string, EvalFunctionConfigSchema | null>>({});
  const [loadingSchemas, setLoadingSchemas] = useState<Record<string, boolean>>({});

  // Fetch config schema for a function
  const fetchFunctionSchema = useCallback(async (functionId: string) => {
    if (functionSchemas[functionId] !== undefined || loadingSchemas[functionId]) {
      return; // Already fetched or loading
    }

    try {
      setLoadingSchemas(prev => ({ ...prev, [functionId]: true }));
      const fn = await evalFunctionApi.get(functionId);
      setFunctionSchemas(prev => ({
        ...prev,
        [functionId]: fn.config_schema || null,
      }));
    } catch (err) {
      console.error('Failed to fetch function schema:', err);
      setFunctionSchemas(prev => ({ ...prev, [functionId]: null }));
    } finally {
      setLoadingSchemas(prev => ({ ...prev, [functionId]: false }));
    }
  }, [functionSchemas, loadingSchemas]);

  // Fetch schemas when functions are selected
  useEffect(() => {
    if (config.correctnessFunction) {
      fetchFunctionSchema(config.correctnessFunction);
    }
  }, [config.correctnessFunction, fetchFunctionSchema]);

  useEffect(() => {
    if (config.qualityMode === 'custom_function' && config.qualityFunctionId) {
      fetchFunctionSchema(config.qualityFunctionId);
    }
  }, [config.qualityMode, config.qualityFunctionId, fetchFunctionSchema]);

  // Helper to update config values for a function
  const handleCorrectnessConfigChange = (key: string, value: unknown) => {
    onChange({
      ...config,
      correctnessFunctionConfig: {
        ...config.correctnessFunctionConfig,
        [key]: value,
      },
    });
  };

  const handleQualityConfigChange = (key: string, value: unknown) => {
    onChange({
      ...config,
      qualityFunctionConfig: {
        ...config.qualityFunctionConfig,
        [key]: value,
      },
    });
  };

  // Build summary of what will happen
  const summary = useMemo(() => {
    const items: { icon: string; text: string }[] = [];

    if (config.correctnessFunction) {
      const fn = availableEvalFunctions.find(f => f.id === config.correctnessFunction);
      const fnName = fn?.display_name || fn?.name || config.correctnessFunction;
      items.push({
        icon: '✗',
        text: `Reject results that fail ${fnName}`,
      });
    }

    if (config.qualityMode === 'llm_judge') {
      items.push({
        icon: '↑',
        text: 'Maximize LLM Judge quality score',
      });
    } else if (config.qualityMode === 'custom_function') {
      const fn = availableEvalFunctions.find(f => f.id === config.qualityFunctionId);
      const fnName = fn?.display_name || fn?.name || 'custom function';
      items.push({
        icon: '↑',
        text: `Maximize ${fnName} score`,
      });
    }

    if (config.optimizeLatency) {
      items.push({ icon: '↓', text: 'Minimize latency' });
    }
    if (config.optimizeCost) {
      items.push({ icon: '↓', text: 'Minimize token cost' });
    }
    if (config.optimizeToolCalls) {
      items.push({ icon: '↓', text: 'Minimize tool calls' });
    }

    if (items.length === 0) {
      items.push({ icon: '↓', text: 'Minimize latency (default)' });
    }

    return items;
  }, [config, availableEvalFunctions]);

  // Helper to render config fields from schema
  const renderConfigFields = (
    functionId: string,
    configValues: Record<string, unknown> | undefined,
    onConfigChange: (key: string, value: unknown) => void
  ) => {
    const schema = functionSchemas[functionId];
    const isLoading = loadingSchemas[functionId];

    if (isLoading) {
      return (
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
          <Loader2 className="h-3 w-3 animate-spin" />
          Loading function config...
        </div>
      );
    }

    // null means fetched but no schema, undefined means not fetched yet
    if (schema === null || schema === undefined) {
      return null;
    }

    if (!schema.properties || Object.keys(schema.properties).length === 0) {
      return null;
    }

    const properties = schema.properties;
    const requiredProps = schema.required || [];

    return (
      <Card className="mt-3 bg-muted/30">
        <CardContent className="pt-3 pb-3">
          <Label className="text-xs font-medium text-muted-foreground mb-2 block">
            Function Parameters
          </Label>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(properties).map(([propKey, propSchema]) => {
              const isRequired = requiredProps.includes(propKey);
              const currentValue = configValues?.[propKey];

              return (
                <div key={propKey} className="space-y-1">
                  <Label className="text-xs">
                    {propKey}
                    {isRequired && <span className="text-red-500 ml-0.5">*</span>}
                  </Label>
                  {propSchema.type === 'boolean' ? (
                    <div className="flex items-center gap-2 h-8">
                      <Checkbox
                        id={`config-${functionId}-${propKey}`}
                        checked={currentValue === true}
                        onCheckedChange={(checked) =>
                          onConfigChange(propKey, checked === true)
                        }
                      />
                      <Label
                        htmlFor={`config-${functionId}-${propKey}`}
                        className="text-xs font-normal"
                      >
                        {propSchema.description || 'Enable'}
                      </Label>
                    </div>
                  ) : propSchema.enum ? (
                    <Select
                      value={String(currentValue ?? propSchema.default ?? '')}
                      onValueChange={(v) => onConfigChange(propKey, v)}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        {propSchema.enum.map((enumVal) => (
                          <SelectItem key={String(enumVal)} value={String(enumVal)}>
                            {String(enumVal)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : propSchema.type === 'number' || propSchema.type === 'integer' ? (
                    <Input
                      type="number"
                      value={typeof currentValue === 'number' ? currentValue : (propSchema.default as number) ?? ''}
                      onChange={(e) =>
                        onConfigChange(
                          propKey,
                          e.target.value ? Number(e.target.value) : undefined
                        )
                      }
                      placeholder={propSchema.description || propKey}
                      className="h-8"
                      min={propSchema.minimum}
                      max={propSchema.maximum}
                    />
                  ) : (
                    <Input
                      type="text"
                      value={String(currentValue ?? propSchema.default ?? '')}
                      onChange={(e) =>
                        onConfigChange(propKey, e.target.value || undefined)
                      }
                      placeholder={propSchema.description || propKey}
                      className="h-8"
                    />
                  )}
                  {propSchema.description && propSchema.type !== 'boolean' && (
                    <p className="text-[10px] text-muted-foreground truncate">
                      {propSchema.description}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Correctness Check */}
      <div className="space-y-2">
        <Label className="text-base font-semibold">Correctness Check</Label>
        <Select
          value={config.correctnessFunction || NONE_VALUE}
          onValueChange={(v) => onChange({
            ...config,
            correctnessFunction: v === NONE_VALUE ? null : v,
            correctnessFunctionConfig: undefined, // Reset config when function changes
          })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select an eval function..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE_VALUE}>
              <span className="text-muted-foreground">None - skip correctness check</span>
            </SelectItem>
            {availableEvalFunctions.map((fn) => (
              <SelectItem key={fn.id} value={fn.id}>
                {fn.display_name || fn.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* Correctness function config */}
        {config.correctnessFunction && renderConfigFields(
          config.correctnessFunction,
          config.correctnessFunctionConfig,
          handleCorrectnessConfigChange
        )}
        <p className="text-sm text-muted-foreground">
          Verifies the output is factually correct.
          {isOptimization && ' Wrong answers = automatic fail, optimizer will avoid them.'}
        </p>
      </div>

      {/* Quality Scoring */}
      <div className="space-y-2">
        <Label className="text-base font-semibold">Quality Scoring</Label>
        <Select
          value={config.qualityMode}
          onValueChange={(v: 'none' | 'llm_judge' | 'custom_function') => onChange({
            ...config,
            qualityMode: v,
            qualityFunctionId: v === 'custom_function' ? config.qualityFunctionId : undefined,
          })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">
              <span className="text-muted-foreground">None - no quality scoring</span>
            </SelectItem>
            <SelectItem value="llm_judge">LLM Judge (scores 0-1)</SelectItem>
            <SelectItem value="custom_function">Custom function</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-sm text-muted-foreground">
          Scores how good the output is.
          {isOptimization && ' Optimizer will maximize this among correct results.'}
        </p>

        {/* LLM Judge settings */}
        {config.qualityMode === 'llm_judge' && (
          <Card className="mt-3">
            <CardContent className="pt-4 space-y-3">
              {/* Judge Agent Selector */}
              <div className="space-y-1">
                <Label className="text-sm">Judge Agent <span className="text-red-500">*</span></Label>
                <Select
                  value={config.judgeAgent || ''}
                  onValueChange={(v) => onChange({ ...config, judgeAgent: v })}
                >
                  <SelectTrigger className={!config.judgeAgent ? 'border-amber-500' : ''}>
                    <SelectValue placeholder="Select a judge agent..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableJudges.length > 0 ? (
                      availableJudges.map((judge) => (
                        <SelectItem key={judge.id} value={judge.path}>
                          <div className="flex flex-col">
                            <span>{judge.name}</span>
                            <span className="text-xs text-muted-foreground">{judge.path}</span>
                          </div>
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="" disabled>
                        No judges available - create one first
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {!config.judgeAgent && (
                  <p className="text-xs text-amber-600">
                    Required: Select a judge agent to evaluate quality
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-sm">Model</Label>
                  <Select
                    value={config.judgeModel || 'anthropic:claude-sonnet-4-5'}
                    onValueChange={(v) => onChange({ ...config, judgeModel: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availableModels.length > 0 ? (
                        availableModels.map((m) => (
                          <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                        ))
                      ) : (
                        <>
                          <SelectItem value="anthropic:claude-sonnet-4-5">Claude Sonnet 4.5</SelectItem>
                          <SelectItem value="anthropic:claude-haiku-3-5">Claude Haiku 3.5</SelectItem>
                          <SelectItem value="openai:gpt-4o">GPT-4o</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">Pass Threshold</Label>
                  <Select
                    value={String(config.judgePassThreshold || 0.7)}
                    onValueChange={(v) => onChange({ ...config, judgePassThreshold: parseFloat(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0.5">0.5 (lenient)</SelectItem>
                      <SelectItem value="0.7">0.7 (default)</SelectItem>
                      <SelectItem value="0.8">0.8 (strict)</SelectItem>
                      <SelectItem value="0.9">0.9 (very strict)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Custom function selector */}
        {config.qualityMode === 'custom_function' && (
          <div className="mt-3 space-y-2">
            <Select
              value={config.qualityFunctionId || ''}
              onValueChange={(v) => onChange({
                ...config,
                qualityFunctionId: v,
                qualityFunctionConfig: undefined, // Reset config when function changes
              })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a scoring function..." />
              </SelectTrigger>
              <SelectContent>
                {availableEvalFunctions.map((fn) => (
                  <SelectItem key={fn.id} value={fn.id}>
                    {fn.display_name || fn.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Quality function config */}
            {config.qualityFunctionId && renderConfigFields(
              config.qualityFunctionId,
              config.qualityFunctionConfig,
              handleQualityConfigChange
            )}
          </div>
        )}
      </div>

      {/* Secondary Metrics */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">Also Optimize For</Label>
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="optimize-latency"
              checked={config.optimizeLatency}
              onCheckedChange={(checked) => onChange({
                ...config,
                optimizeLatency: !!checked,
              })}
            />
            <label htmlFor="optimize-latency" className="text-sm cursor-pointer">
              Latency (minimize response time)
            </label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="optimize-cost"
              checked={config.optimizeCost}
              onCheckedChange={(checked) => onChange({
                ...config,
                optimizeCost: !!checked,
              })}
            />
            <label htmlFor="optimize-cost" className="text-sm cursor-pointer">
              Cost (minimize token usage)
            </label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="optimize-tools"
              checked={config.optimizeToolCalls}
              onCheckedChange={(checked) => onChange({
                ...config,
                optimizeToolCalls: !!checked,
              })}
            />
            <label htmlFor="optimize-tools" className="text-sm cursor-pointer">
              Tool calls (minimize number of tool invocations)
            </label>
          </div>
        </div>
      </div>

      {/* Summary */}
      {isOptimization && (
        <Card className="bg-muted/50">
          <CardContent className="pt-4">
            <p className="text-sm font-medium mb-2">The optimizer will:</p>
            <ul className="space-y-1">
              {summary.map((item, i) => (
                <li key={i} className="text-sm flex items-center gap-2">
                  <span className="w-4 text-center font-mono">{item.icon}</span>
                  <span>{item.text}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default EvaluationSetup;
